import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import useAuthStore from "../store/useAuthStore";
import { toast } from "react-toastify";
import { ReactComponent as GalleryIcon } from "../assets/gallery-send-svgrepo-com.svg";
import { ReactComponent as CameraIcon } from "../assets/photo-camera-photocamera-svgrepo-com.svg";
import { addTag, getTags } from "../services/TagServices";
import { saveCard, scanCard } from "../services/uploadServices";
import {
  getAvailableProfiles,
  setWorkspaceProfile,
} from "../services/ProfileServices";

const loadImage = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file);
    } catch {
      // Some mobile formats need the image-element fallback.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  const image = document.createElement("img");

  try {
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = objectUrl;
    });
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const optimizeImage = async (
  file,
  { maxWidth = 1280, maxHeight = 960, quality = 0.8 } = {}
) => {
  const startedAt = performance.now();
  const image = await loadImage(file);
  const sourceWidth = image.naturalWidth || image.width;
  const sourceHeight = image.naturalHeight || image.height;
  const scale = Math.min(1, maxWidth / sourceWidth, maxHeight / sourceHeight);
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Unable to optimize the selected image");
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, width, height);
  image.close?.();

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) =>
        result
          ? resolve(result)
          : reject(new Error("Unable to optimize the selected image")),
      "image/jpeg",
      quality
    );
  });
  const optimizedFile = new File(
    [blob],
    `${file.name.replace(/\.[^.]+$/, "")}.jpg`,
    {
      type: "image/jpeg",
      lastModified: Date.now(),
    }
  );

  console.info("Card image optimization", {
    originalBytes: file.size,
    optimizedBytes: optimizedFile.size,
    reductionPercent:
      file.size > 0
        ? Math.round((1 - optimizedFile.size / file.size) * 100)
        : 0,
    durationMs: Math.round(performance.now() - startedAt),
    dimensions: `${width}x${height}`,
  });

  return optimizedFile;
};

const CheckIcon = ({ className = "h-4 w-4" }) => (
  <svg
    aria-hidden="true"
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m5 12 4 4L19 6" />
  </svg>
);

const SparkleIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2L12 3Z" />
    <path d="m18.5 14 .8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8.8-2.2Z" />
    <path d="m5 13 .8 2.2L8 16l-2.2.8L5 19l-.8-2.2L2 16l2.2-.8L5 13Z" />
  </svg>
);

const WorkflowSteps = ({ hasImage, hasCard }) => {
  const steps = ["Photo", "Review", "Save"];
  const currentStep = hasCard ? 2 : 1;

  return (
    <ol
      className="flex shrink-0 items-center gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-900"
      aria-label="Card scanning progress"
    >
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const complete = stepNumber < currentStep || (hasImage && index === 0 && hasCard);
        const active = stepNumber === currentStep;

        return (
          <React.Fragment key={step}>
            <li
              className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-bold transition ${
                active
                  ? "bg-white text-indigo-700 shadow-sm dark:bg-slate-800 dark:text-indigo-300"
                  : complete
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-slate-400 dark:text-slate-500"
              }`}
              aria-current={active ? "step" : undefined}
              aria-label={`Step ${stepNumber}: ${step}`}
            >
              <span
                className={`h-1.5 w-1.5 shrink-0 rounded-full transition ${
                  complete
                    ? "bg-emerald-500"
                    : active
                      ? "bg-indigo-600"
                      : "bg-slate-300 dark:bg-slate-600"
                }`}
              />
              <span>{step}</span>
            </li>
            {index < steps.length - 1 && (
              <span
                aria-hidden="true"
                className="text-[10px] text-slate-300 dark:text-slate-700"
              >
                /
              </span>
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
};

const PhotoSourcePicker = ({
  id,
  label,
  busy,
  onChange,
  className,
  iconClassName = "h-5 w-5",
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const handleImageChange = (event) => {
    if (!event.target.files?.length) return;
    setOpen(false);
    onChange(event);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={busy}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={className}
      >
        <CameraIcon className={iconClassName} /> {label}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-950/65 p-3 backdrop-blur-sm sm:items-center"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setOpen(false);
          }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${id}-source-title`}
            className="mobile-safe-bottom w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-4 shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:p-5"
          >
            <div className="px-1">
              <h2
                id={`${id}-source-title`}
                className="text-lg font-bold text-slate-950 dark:text-white"
              >
                Add card photo
              </h2>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Take a new picture or select an existing image.
              </p>
            </div>

            <div className="mt-4 grid gap-2">
              <label
                htmlFor={`${id}-camera`}
                className="flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl bg-indigo-600 px-4 text-sm font-bold text-white transition hover:bg-indigo-700"
              >
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/15">
                  <CameraIcon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block">Take photo</span>
                  <span className="mt-0.5 block text-[11px] font-medium text-indigo-100">
                    Opens the rear camera
                  </span>
                </span>
                <input
                  id={`${id}-camera`}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="sr-only"
                  onChange={handleImageChange}
                />
              </label>

              <label
                htmlFor={`${id}-gallery`}
                className="flex min-h-14 cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 px-4 text-sm font-bold text-slate-800 transition hover:border-indigo-300 hover:bg-indigo-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-indigo-600 dark:hover:bg-indigo-500/10"
              >
                <span className="grid h-9 w-9 place-items-center rounded-xl bg-slate-100 text-indigo-600 dark:bg-slate-800 dark:text-indigo-300">
                  <GalleryIcon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block">Choose from gallery</span>
                  <span className="mt-0.5 block text-[11px] font-medium text-slate-500 dark:text-slate-400">
                    Select a saved image
                  </span>
                </span>
                <input
                  id={`${id}-gallery`}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handleImageChange}
                />
              </label>
            </div>

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="mt-3 min-h-11 w-full rounded-xl text-sm font-bold text-slate-500 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
          </section>
        </div>
      )}
    </>
  );
};

const ImagePickerCard = ({
  id,
  title,
  description,
  preview,
  required,
  busy,
  onChange,
  onRemove,
  onCollapse,
}) => (
  <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
    <div className="flex items-center justify-between gap-3 px-4 py-3.5 sm:px-5">
      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-white">{title}</h3>
        {description && (
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{description}</p>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span
          className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
            required
              ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300"
              : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
          }`}
        >
          {required ? "Required" : "Optional"}
        </span>
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            className="rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            Hide
          </button>
        )}
      </div>
    </div>

    {preview ? (
      <div className="px-3 pb-3 sm:px-4 sm:pb-4">
        <div className="relative aspect-[16/10] overflow-hidden rounded-2xl bg-slate-950">
          <img
            src={preview}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full scale-110 object-cover opacity-25 blur-2xl"
          />
          <span className="absolute inset-0 bg-slate-950/20" />
          <img
            src={preview}
            alt={`${title} preview`}
            className="relative h-full w-full object-contain p-2 sm:p-3"
          />
          <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-2.5 py-1 text-[11px] font-bold text-white shadow-lg">
            <CheckIcon className="h-3.5 w-3.5" /> Ready
          </span>
          {busy && (
            <div className="absolute inset-0 grid place-items-center bg-slate-950/65 text-white">
              <ClipLoader size={24} color="#ffffff" />
            </div>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <PhotoSourcePicker
            id={`${id}-replace`}
            label="Change photo"
            busy={busy}
            onChange={onChange}
            className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-700 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 dark:border-slate-700 dark:text-slate-200 dark:hover:border-indigo-600 dark:hover:bg-indigo-500/10 dark:hover:text-indigo-300"
            iconClassName="h-4 w-4"
          />
          <button
            type="button"
            onClick={onRemove}
            disabled={busy}
            className="min-h-11 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-600 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:border-rose-800 dark:hover:bg-rose-500/10 dark:hover:text-rose-300"
          >
            Remove
          </button>
        </div>
      </div>
    ) : (
      <div className="mx-3 mb-3 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 px-4 py-7 dark:border-slate-700 dark:bg-slate-950 sm:mx-4 sm:mb-4 sm:px-6 sm:py-9">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-white text-indigo-600 shadow-sm ring-1 ring-slate-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-400/20">
          {busy ? <ClipLoader size={22} color="#4f46e5" /> : <CameraIcon className="h-7 w-7" />}
        </div>
        <div className="mx-auto mt-5 max-w-sm">
          <PhotoSourcePicker
            id={`${id}-photo`}
            label="Add photo"
            busy={busy}
            onChange={onChange}
            className="inline-flex min-h-12 w-full cursor-pointer items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
          />
        </div>
      </div>
    )}
  </section>
);

const Field = ({ label, name, value, onChange, type = "text", placeholder }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold text-slate-600">
      {label}
    </span>
    <input
      className="min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-[15px] text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
    />
  </label>
);

const PhotoUploadPage = () => {
  const token = useAuthStore((state) => state.token);
  const navigate = useNavigate();
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [userFile, setUserFile] = useState(null);
  const [userPreview, setUserPreview] = useState(null);
  const [imageProcessing, setImageProcessing] = useState("");
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [visitingCard, setVisitingCard] = useState(null);
  const [profileList, setProfileList] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [profilePreferenceSaving, setProfilePreferenceSaving] = useState(false);
  const [tagsList, setTagsList] = useState([]);
  const [additionalImagesOpen, setAdditionalImagesOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (frontPreview) URL.revokeObjectURL(frontPreview);
    };
  }, [frontPreview]);

  useEffect(() => {
    return () => {
      if (backPreview) URL.revokeObjectURL(backPreview);
    };
  }, [backPreview]);

  useEffect(() => {
    return () => {
      if (userPreview) URL.revokeObjectURL(userPreview);
    };
  }, [userPreview]);

  const allProfiles = async () => {
    const res = await getAvailableProfiles();
    const profiles = (res?.data?.profiles || []).filter(
      (profile) =>
        profile.isSelectable !== false &&
        (!profile.status || profile.status === "PUBLISHED")
    );
    setProfileList(profiles);
    setSelectedProfileId(res?.data?.workspaceCampaignId || "");
  };

  const loadTags = async () => {
    const res = await getTags();
    setTagsList(res?.data || []);
  };

  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    Promise.all([allProfiles(), loadTags()]).catch((error) => {
      console.error("Error loading upload options:", error);
      toast.error("Some card options could not be loaded");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const processImage = async (event, type) => {
    const input = event.target;
    const selectedFile = input.files?.[0];
    if (!selectedFile) return;

    setImageProcessing(type);
    try {
      const optimizedFile = await optimizeImage(selectedFile);
      const previewUrl = URL.createObjectURL(optimizedFile);

      if (type === "front") {
        setFrontFile(optimizedFile);
        setFrontPreview(previewUrl);
        setVisitingCard(null);
      } else if (type === "back") {
        setBackFile(optimizedFile);
        setBackPreview(previewUrl);
        setVisitingCard(null);
      } else {
        setUserFile(optimizedFile);
        setUserPreview(previewUrl);
      }
    } catch (error) {
      console.error("Error preparing image:", error);
      toast.error("This image could not be prepared. Please choose another one.");
    } finally {
      setImageProcessing("");
      input.value = "";
    }
  };

  const resetImages = () => {
    setFrontFile(null);
    setBackFile(null);
    setUserFile(null);
    setFrontPreview(null);
    setBackPreview(null);
    setUserPreview(null);
    setImageProcessing("");
    setAdditionalImagesOpen(false);
  };

  const handleRemoveFront = () => {
    resetImages();
    setVisitingCard(null);
  };

  const handleRemoveBack = () => {
    setBackFile(null);
    setBackPreview(null);
    setVisitingCard(null);
  };

  const handlePhotoUpload = async () => {
    if (!frontFile) {
      toast.error("Add the front of the card before scanning");
      return;
    }

    const formData = new FormData();
    formData.append("front", frontFile);
    if (backFile) formData.append("back", backFile);

    try {
      setLoading(true);
      const scanStartedAt = performance.now();
      const res = await scanCard(formData);
      const data = res.data;

      if (res.status === 200) {
        console.info("Card scan timing", {
          clientTotalMs: Math.round(performance.now() - scanStartedAt),
          azureScanMs: data.timing?.scanTimeMs,
        });
        setVisitingCard(data.visitingCard);
        toast.success("Scan complete. Review the details before saving.");
        requestAnimationFrame(() => {
          document
            .getElementById("card-details")
            ?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } else {
        toast.error(data.message || "Card scan failed");
      }
    } catch (error) {
      console.error("Error during card scan:", error);
      toast.error(error.response?.data?.message || "Card scan failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setVisitingCard((currentCard) => ({ ...currentCard, [name]: value }));
  };

  const handleProfileChange = async (event) => {
    const nextProfileId = event.target.value;
    const previousProfileId = selectedProfileId;

    setSelectedProfileId(nextProfileId);
    setProfilePreferenceSaving(true);
    try {
      await setWorkspaceProfile(nextProfileId);
    } catch (error) {
      setSelectedProfileId(previousProfileId);
      toast.error(
        error.response?.data?.message ||
          "Your default campaign could not be updated"
      );
    } finally {
      setProfilePreferenceSaving(false);
    }
  };

  const handleAddTag = async () => {
    const enteredName = window.prompt("Enter a new tag name:");
    const name = enteredName?.trim();
    if (!name) return;

    try {
      await addTag(name);
      await loadTags();
      setVisitingCard((currentCard) => ({ ...currentCard, tag: name }));
      toast.success("Tag added");
    } catch (error) {
      toast.error(error.response?.data?.message || "Tag could not be added");
    }
  };

  const handleSave = async () => {
    if (!visitingCard || !frontFile) return;

    try {
      setUpdating(true);
      const saveStartedAt = performance.now();
      const formData = new FormData();
      formData.append("profileId", selectedProfileId);
      formData.append("company", visitingCard.company || "");
      formData.append("position", visitingCard.position || "");
      formData.append("firstName", visitingCard.firstName || "");
      formData.append("lastName", visitingCard.lastName || "");
      formData.append("mobile", visitingCard.mobile || "");
      formData.append("email", visitingCard.email || "");
      formData.append("website", visitingCard.website || "");
      formData.append("remark", visitingCard.remark || "");
      formData.append("tag", visitingCard.tag || "");
      formData.append("metadata", visitingCard.metadata || "{}");
      formData.append("front", frontFile);
      if (backFile) formData.append("back", backFile);
      if (userFile) formData.append("userImage", userFile);

      const res = await saveCard(formData);
      const data = res.data;

      if (res.status === 201 || res.status === 200) {
        console.info("Card save timing", {
          clientTotalMs: Math.round(performance.now() - saveStartedAt),
          ...data.timing,
          queuedJobs: data.automationJobs?.map((job) => job.type) || [],
        });
        toast.success("Card saved. Automations are processing.");
        setVisitingCard(null);
        resetImages();
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        toast.error(data.message || "Card could not be saved");
      }
    } catch (error) {
      console.error("Error saving visiting card:", error);
      toast.error(error.response?.data?.message || "Card could not be saved. Try again.");
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    setVisitingCard(null);
    resetImages();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="w-full px-4 py-5 sm:px-6 sm:py-8 lg:px-8">
      <div className="mx-auto max-w-4xl">
        <header className="mb-5 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
            Scan card
          </h1>
          <WorkflowSteps hasImage={Boolean(frontPreview)} hasCard={Boolean(visitingCard)} />
        </header>

        <motion.section
          className="mx-auto max-w-3xl"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.06 }}
        >
          <div>
            <ImagePickerCard
              id="front-card"
              title="Front of card"
              description=""
              preview={frontPreview}
              required
              busy={imageProcessing === "front"}
              onChange={(event) => processImage(event, "front")}
              onRemove={handleRemoveFront}
            />

          </div>
          {frontPreview && (
            <div className="mt-4">
              {!additionalImagesOpen ? (
                <button
                  type="button"
                  onClick={() => setAdditionalImagesOpen(true)}
                  aria-expanded="false"
                  aria-controls="back-card-panel"
                  className="flex min-h-14 w-full items-center justify-between rounded-2xl border border-dashed border-slate-300 bg-white px-4 text-left shadow-sm transition hover:border-indigo-300 hover:bg-indigo-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-indigo-600 dark:hover:bg-indigo-500/10 sm:px-5"
                >
                  <span className="flex items-center gap-3">
                    <span className="grid h-9 w-9 place-items-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300">
                      <GalleryIcon className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-sm font-bold text-slate-800 dark:text-white">Add back of card</span>
                      <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">Optional</span>
                    </span>
                  </span>
                  <span className="text-xl leading-none text-slate-400" aria-hidden="true">+</span>
                </button>
              ) : (
                <div id="back-card-panel">
                  <ImagePickerCard
                    id="back-card"
                    title="Back of card"
                    description=""
                    preview={backPreview}
                    busy={imageProcessing === "back"}
                    onChange={(event) => processImage(event, "back")}
                    onRemove={handleRemoveBack}
                    onCollapse={() => setAdditionalImagesOpen(false)}
                  />
                </div>
              )}
            </div>
          )}

          {frontPreview && (
            <div className="mt-4">
              <button
                type="button"
                onClick={handlePhotoUpload}
                disabled={loading || Boolean(imageProcessing)}
                className="inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-6 text-sm font-bold text-white shadow-lg shadow-indigo-200 transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 dark:shadow-indigo-950/40"
              >
                {loading ? (
                  <>
                    <ClipLoader size={18} color="#ffffff" /> Scanning card…
                  </>
                ) : (
                  <>
                    <SparkleIcon /> {visitingCard ? "Scan again" : "Scan card"}
                  </>
                )}
              </button>
            </div>
          )}
        </motion.section>

        {visitingCard && (
          <motion.section
            id="card-details"
            className="scroll-mt-32 mt-5 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm sm:mt-6"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
          >
            <div className="border-b border-slate-100 px-4 py-5 sm:px-6 sm:py-6">
              <div className="flex items-start gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-100 text-emerald-700">
                  <CheckIcon className="h-5 w-5" />
                </span>
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-emerald-600">
                    Step 2 · Scan complete
                  </span>
                  <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-950">
                    Review contact details
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Correct anything the scanner missed before saving.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="First name" name="firstName" value={visitingCard.firstName} onChange={handleFieldChange} placeholder="First name" />
                <Field label="Last name" name="lastName" value={visitingCard.lastName} onChange={handleFieldChange} placeholder="Last name" />
                <Field label="Company" name="company" value={visitingCard.company} onChange={handleFieldChange} placeholder="Company name" />
                <Field label="Job title" name="position" value={visitingCard.position} onChange={handleFieldChange} placeholder="Designation or role" />
                <Field label="Mobile" name="mobile" type="tel" value={visitingCard.mobile} onChange={handleFieldChange} placeholder="Include country code" />
                <Field label="Email" name="email" type="email" value={visitingCard.email} onChange={handleFieldChange} placeholder="name@company.com" />
                <div className="sm:col-span-2">
                  <Field label="Website" name="website" type="url" value={visitingCard.website} onChange={handleFieldChange} placeholder="https://company.com" />
                </div>
              </div>

              <div className="my-6 h-px bg-slate-100" />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-600">Campaign</span>
                  <select
                    className="min-h-12 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-[15px] text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                    value={selectedProfileId}
                    onChange={handleProfileChange}
                    disabled={profilePreferenceSaving}
                    aria-busy={profilePreferenceSaving}
                  >
                    <option value="">No campaign automation</option>
                    {profileList.some((profile) => profile.visibility === "SHARED") && (
                      <optgroup label="Workspace campaigns">
                        {profileList
                          .filter((profile) => profile.visibility === "SHARED")
                          .map((profile) => (
                            <option key={profile.id} value={profile.id}>
                              {profile.name}
                            </option>
                          ))}
                      </optgroup>
                    )}
                    {profileList.some((profile) => profile.visibility === "PRIVATE") && (
                      <optgroup label="My campaigns">
                        {profileList
                          .filter((profile) => profile.visibility === "PRIVATE")
                          .map((profile) => (
                            <option key={profile.id} value={profile.id}>
                              {profile.name}
                            </option>
                          ))}
                      </optgroup>
                    )}
                  </select>
                  <span className="mt-1.5 block text-[11px] leading-4 text-slate-400">
                    {profilePreferenceSaving
                      ? "Saving as your default…"
                      : "Your last selection is used automatically for the next card."}
                  </span>
                </label>

                <label className="block">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-600">Tag</span>
                  <span className="flex gap-2">
                    <select
                      name="tag"
                      value={visitingCard.tag || ""}
                      onChange={handleFieldChange}
                      className="min-h-12 min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3.5 text-[15px] text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                    >
                      <option value="">No tag</option>
                      {tagsList.map((tag) => (
                        <option key={tag.id || tag.name} value={tag.name}>{tag.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="min-h-12 shrink-0 rounded-xl border border-indigo-200 bg-indigo-50 px-3.5 text-sm font-bold text-indigo-700 transition hover:bg-indigo-100"
                    >
                      + Add
                    </button>
                  </span>
                </label>

                <label className="block sm:col-span-2">
                  <span className="mb-1.5 block text-xs font-semibold text-slate-600">Remark</span>
                  <textarea
                    className="min-h-24 w-full resize-y rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-[15px] text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                    name="remark"
                    value={visitingCard.remark || ""}
                    onChange={handleFieldChange}
                    placeholder="Add a note about this contact"
                  />
                </label>
              </div>

              <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Visitor photo</h3>
                    <p className="mt-1 text-xs leading-5 text-slate-500">
                      Optional. Add a face photo to recognize the contact later.
                    </p>
                  </div>
                  <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-500 ring-1 ring-slate-200">Optional</span>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-[7rem_1fr] sm:items-center">
                  <div className="relative mx-auto h-28 w-28 overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200 sm:mx-0">
                    {userPreview ? (
                      <img src={userPreview} alt="Visitor preview" className="h-full w-full object-cover" />
                    ) : (
                      <div className="grid h-full w-full place-items-center text-slate-300">
                        <svg aria-hidden="true" className="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                          <circle cx="12" cy="8" r="4" />
                          <path d="M4 21a8 8 0 0 1 16 0" />
                        </svg>
                      </div>
                    )}
                    {imageProcessing === "visitor" && (
                      <div className="absolute inset-0 grid place-items-center bg-white/80 dark:bg-slate-950/80">
                        <ClipLoader size={22} color="#4f46e5" />
                      </div>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <PhotoSourcePicker
                      id="visitor-photo"
                      label={userPreview ? "Change photo" : "Add photo"}
                      busy={imageProcessing === "visitor"}
                      onChange={(event) => processImage(event, "visitor")}
                      className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                      iconClassName="h-4 w-4"
                    />
                    {userPreview && (
                      <button type="button" onClick={() => { setUserFile(null); setUserPreview(null); }} className="min-h-10 text-sm font-semibold text-rose-600">
                        Remove visitor photo
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mobile-safe-bottom sticky bottom-0 z-20 border-t border-slate-200 bg-white/95 p-4 shadow-[0_-12px_30px_-24px_rgba(15,23,42,0.7)] backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 sm:static sm:flex sm:justify-end sm:gap-3 sm:px-6 sm:py-5 sm:shadow-none">
              <button type="button" onClick={handleCancel} disabled={updating} className="mb-2 min-h-12 w-full rounded-xl border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 sm:mb-0 sm:w-auto">
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={updating || profilePreferenceSaving || Boolean(imageProcessing)} className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-7 text-sm font-bold text-white shadow-lg shadow-indigo-200 transition hover:from-indigo-700 hover:to-violet-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto">
                {updating ? (
                  <><ClipLoader size={18} color="#ffffff" /> Saving card…</>
                ) : (
                  <><CheckIcon className="h-4 w-4" /> Save card</>
                )}
              </button>
            </div>
          </motion.section>
        )}
      </div>
    </main>
  );
};

export default PhotoUploadPage;
